/**
 * GuidedTour — reusable coachmark tour primitive built on react-joyride v3.
 *
 * v3 migration notes (iter150AZ.65):
 *   • `callback` → `onEvent` (v3 renamed the prop and now emits only
 *     lifecycle EVENTS/STATUS; the host must advance ``stepIndex``
 *     manually in controlled mode).
 *   • `styles={{options:{}}}` + top-level `showSkipButton`/`showProgress`
 *     merged into a single flat ``options={{…}}`` prop.
 *   • Component is now CONTROLLED (`stepIndex` state) so we can react
 *     to STEP_AFTER + TARGET_NOT_FOUND cleanly and guarantee the
 *     finish-state hits STATUS.FINISHED (which is what persists the
 *     "don't-show-again" flag to localStorage).
 *
 * Layered behaviour (unchanged public contract — {steps, storageKey,
 * runFlag, onFinish}):
 *   • First-visit auto-run on DESKTOP only — mobile users get a stable
 *     screen until they explicitly tap the "?" Help button. Mobile
 *     auto-run is disabled because react-joyride v3's portal + scroll
 *     handling has known issues inside the absolute-positioned, full-
 *     screen customer-detail panel and was causing blank screens on
 *     Android Chrome (iter150AZ.46).
 *   • Manual re-launch: increment ``runFlag`` (e.g. from a Help button)
 *     to re-show the tour at any time. This works on every viewport.
 *   • Hard isolation: the Joyride tree is wrapped in an error boundary
 *     so any internal failure leaves the host page intact.
 *
 * Steps must target ``[data-tour="<id>"]`` selectors on host elements
 * so the tour stays decoupled from refactors of the surrounding
 * components.
 */
import { useEffect, useRef, useState, useCallback, Component } from "react";
import { Joyride, STATUS, EVENTS, ACTIONS } from "react-joyride";
import { Question } from "@phosphor-icons/react";
import { useT, useI18n } from "@/lib/i18n";

// Below this viewport width we skip auto-run. Joyride's portal can
// fight the mobile customer-detail's nested-scroll layout and crash
// the React subtree → blank screen. The Help button still works.
const MOBILE_AUTORUN_BREAKPOINT = 768;

class TourErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error) {
    // Swallow the failure so the host page stays mounted. Log so we
    // can still spot bugs in PostHog / console.
    try {
      // eslint-disable-next-line no-console
      console.warn("[GuidedTour] suppressed Joyride error:", error);
    } catch { /* ignore */ }
  }
  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

function _firstAnchorPresent(steps) {
  // Returns true when at least one step's target is already in the
  // DOM. Defers auto-run until the host has rendered something
  // Joyride can latch onto.
  if (!steps || !steps.length) return false;
  for (const s of steps) {
    if (!s?.target) continue;
    try {
      if (document.querySelector(s.target)) return true;
    } catch { /* ignore invalid selector */ }
  }
  return false;
}

export default function GuidedTour({ steps, storageKey, runFlag, onFinish }) {
  const t = useT();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const persistedRef = useRef(false);

  const finish = useCallback(() => {
    setRun(false);
    setStepIndex(0);
    if (!persistedRef.current) {
      persistedRef.current = true;
      try { if (storageKey) localStorage.setItem(storageKey, "1"); }
      catch { /* ignore */ }
      onFinish && onFinish();
    }
  }, [storageKey, onFinish]);

  const begin = useCallback(() => {
    persistedRef.current = false;
    setStepIndex(0);
    setRun(true);
  }, []);

  // Auto-launch on first visit (desktop only, and only after at least
  // one anchor is in the DOM).
  useEffect(() => {
    if (!storageKey) return;
    let cancelled = false;
    try {
      const done = localStorage.getItem(storageKey);
      if (done) return;
      if (typeof window !== "undefined"
          && window.innerWidth < MOBILE_AUTORUN_BREAKPOINT) {
        return; // mobile — wait for explicit Help-button tap
      }
      // Poll briefly until the first anchor renders, then start.
      let tries = 0;
      const tick = () => {
        if (cancelled) return;
        tries += 1;
        if (_firstAnchorPresent(steps)) { begin(); return; }
        if (tries < 10) setTimeout(tick, 400); // give up after ~4 s
      };
      const id = setTimeout(tick, 600);
      return () => { cancelled = true; clearTimeout(id); };
    } catch { /* private mode — skip */ }
  }, [storageKey, steps, begin]);

  // External trigger (Help button).
  useEffect(() => {
    if (runFlag) begin();
  }, [runFlag, begin]);

  // v3 event handler — advance stepIndex manually and detect terminal
  // states so persistence actually fires.
  const handleEvent = useCallback((data) => {
    const { status, type, index, action } = data || {};
    if (status === STATUS.FINISHED
        || status === STATUS.SKIPPED
        || status === STATUS.ERROR
        || (action === ACTIONS.CLOSE && type === EVENTS.STEP_AFTER)) {
      finish();
      return;
    }
    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const next = index + (action === ACTIONS.PREV ? -1 : 1);
      if (next < 0 || next >= steps.length) { finish(); return; }
      setStepIndex(next);
    }
  }, [steps, finish]);

  if (!steps || steps.length === 0) return null;

  return (
    <TourErrorBoundary>
      <Joyride
        steps={steps}
        run={run}
        stepIndex={stepIndex}
        continuous
        onEvent={handleEvent}
        options={{
          skipBeacon: true,
          showProgress: true,
          buttons: ["back", "close", "primary", "skip"],
          closeButtonAction: "skip",
          overlayClickAction: false,
          targetWaitTimeout: 3000,
          disableScrolling: false,
          primaryColor: "#1d4ed8",
          zIndex: 10000,
          arrowColor: "#fff",
          backgroundColor: "#fff",
          textColor: "#1e293b",
        }}
        locale={{
          back: t("tour.back", "Back"),
          close: t("tour.close", "Close"),
          last: t("tour.done", "Done"),
          next: t("tour.next", "Next"),
          skip: t("tour.skip", "Skip"),
        }}
      />
    </TourErrorBoundary>
  );
}

/**
 * Small "?" pill + cheat-sheet print link. Clicking the ? re-triggers
 * the tour. The "Print" link opens the language-aware cheat-sheet PDF
 * in a new tab so the user can print or share it.
 */
export function TourHelpButton({ onClick, testid = "tour-help-button", scope = "" }) {
  const t = useT();
  const { lang } = useI18n();
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  // iter150AZ.64 — scoped cheat-sheet. The per-page tour button passes
  // its scope ("customer_detail", "pm_viewer", "quote_detail", …) so
  // the PDF that opens covers ONLY that screen's steps.
  const cheatHref =
    `${apiBase}/api/i18n/cheatsheet.pdf?lang=${encodeURIComponent(lang || "en")}`
    + (scope ? `&scope=${encodeURIComponent(scope)}` : "");
  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        data-testid={testid}
        onClick={onClick}
        title={t("tour.help_button", "Show the guided tour")}
        className="inline-flex items-center gap-1 h-7 px-2 rounded-sm border text-[10px] font-bold uppercase tracking-wider bg-white text-blue-700 border-blue-200 hover:bg-blue-50 transition-colors"
      >
        <Question size={11} weight="bold" />
        <span className="hidden md:inline">{t("tour.help_label", "Help")}</span>
      </button>
      <a
        href={cheatHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${testid}-cheatsheet`}
        title={t("tour.cheatsheet_link", "Print cheat-sheet")}
        className="inline-flex items-center gap-1 h-7 px-2 rounded-sm border text-[10px] font-bold uppercase tracking-wider bg-white text-slate-600 border-slate-200 hover:bg-slate-50 transition-colors"
      >
        <span className="hidden md:inline">{t("tour.cheatsheet_link", "Print cheat-sheet")}</span>
        <span className="md:hidden">PDF</span>
      </a>
    </span>
  );
}
